import { t } from "i18next";
import {
  AlertTriangle,
  CheckCircle2,
  CloudOff,
  Download,
  Loader2,
  Mic,
  RotateCw,
  Trash2,
} from "lucide-react";
import type { JSX } from "react";
import { Button } from "@/components/ui/button";
import { uiDateLocale } from "@/lib/date-locale";
import {
  hasPublicRecordingIdentity,
  offlineIdentityUpgrade,
} from "@/lib/offline-recording/identity";
import { isExpiringSoon } from "@/lib/offline-recording/limits";
import {
  isRawValidationError,
  SETTINGS_REJECTED,
} from "@/lib/offline-recording/queue";
import type { OfflineRecording } from "@/lib/offline-recording/types";
import { cn } from "@/lib/utils";
import {
  formatExpiry,
  formatRecordingDuration,
  formatRecordingStart,
  formatSize,
} from "./format";

export type RecordingCardKind = "recovered" | "queued";

export interface RecordingCardProps {
  recording: OfflineRecording;
  kind: RecordingCardKind;
  online: boolean;
  progress?: number;
  now?: number;
  busy?: boolean;
  onProcess?: () => void;
  onRetry?: () => void;
  onDownload: () => void;
  onDiscard: () => void;
}

const time = (ms: number): string =>
  new Date(ms).toLocaleTimeString(uiDateLocale(), {
    hour: "2-digit",
    minute: "2-digit",
  });

const statusOf = (
  recording: OfflineRecording,
  kind: RecordingCardKind,
  online: boolean,
  progress: number | undefined,
): { label: string; tone: "neutral" | "busy" | "warning" | "error" } => {
  if (kind === "recovered")
    return {
      label: t("offlineRecording.status.notSubmitted", "Not submitted"),
      tone: "neutral",
    };
  switch (recording.status) {
    case "uploading":
      return {
        label:
          progress !== undefined && progress > 0
            ? t(
                "offlineRecording.status.uploadingPercent",
                "Uploading {{percent}}%",
                {
                  percent: progress,
                },
              )
            : t("offlineRecording.status.uploading", "Uploading…"),
        tone: "busy",
      };
    case "failed":
      if (recording.upload?.blocked)
        return {
          label: t("offlineRecording.status.blocked", "Needs attention"),
          tone: "error",
        };
      return {
        label: online
          ? t("offlineRecording.status.retrying", "Retrying at {{time}}", {
              time: time(recording.upload?.nextAttemptAt ?? Date.now()),
            })
          : t(
              "offlineRecording.status.waitingOnline",
              "Waiting for connection",
            ),
        tone: "warning",
      };
    case "uncertain":
      return {
        label: t("offlineRecording.status.uncertain", "Check Visits"),
        tone: "error",
      };
    default:
      return {
        label: online
          ? t("offlineRecording.status.queued", "Waiting to upload")
          : t(
              "offlineRecording.status.waitingOnline",
              "Waiting for connection",
            ),
        tone: online ? "busy" : "warning",
      };
  }
};

/** One recording kept on this device, in the recovery dialog or the pending list. */
export const RecordingCard = ({
  recording,
  kind,
  online,
  progress,
  now = Date.now(),
  busy = false,
  onProcess,
  onRetry,
  onDownload,
  onDiscard,
}: RecordingCardProps): JSX.Element => {
  const obsolete = !hasPublicRecordingIdentity(recording);
  const status = statusOf(recording, kind, online, progress);
  const start = formatRecordingStart(recording.createdAt, new Date(now));
  const duration = formatRecordingDuration(recording.durationMs);
  const title =
    kind === "recovered"
      ? t(
          "offlineRecording.card.recoveredTitle",
          "Recovered recording from {{start}} ({{duration}})",
          { start, duration },
        )
      : t(
          "offlineRecording.card.title",
          "Recording from {{start}} ({{duration}})",
          {
            start,
            duration,
          },
        );
  const canRetry =
    !obsolete &&
    kind === "queued" &&
    (recording.status === "failed" || recording.status === "uncertain");
  const uploading = recording.status === "uploading";
  const error = kind === "queued" ? recording.upload?.lastError : null;
  const StatusIcon =
    status.tone === "busy"
      ? uploading
        ? Loader2
        : CheckCircle2
      : status.tone === "neutral"
        ? Mic
        : status.tone === "warning" && !online
          ? CloudOff
          : AlertTriangle;

  return (
    <article
      aria-label={title}
      className="flex flex-col gap-3 rounded-2xl border bg-card p-4 text-card-foreground shadow-xs"
      data-testid="offline-recording-card"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"
        >
          <Mic className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <h3 className="text-base font-semibold leading-snug">{title}</h3>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
                status.tone === "neutral" && "bg-muted text-foreground",
                status.tone === "busy" && "bg-primary/10 text-primary",
                status.tone === "warning" &&
                  "bg-warning/15 text-foreground dark:text-warning",
                status.tone === "error" && "bg-error/10 text-error",
              )}
            >
              <StatusIcon
                aria-hidden="true"
                className={cn("h-3.5 w-3.5", uploading && "animate-spin")}
              />
              {status.label}
            </span>
            <span>{formatSize(recording.bytes)}</span>
          </div>
        </div>
      </div>
      {uploading && progress !== undefined && (
        <div
          aria-label={t("offlineRecording.card.progress", "Upload progress")}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={progress}
          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
        >
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      {obsolete && (
        <p role="alert" className="text-sm leading-5 text-error">
          {offlineIdentityUpgrade()}{" "}
          {recording.upload || recording.submitAttempted
            ? t("offlineRecording.errors.discardOldRequest")
            : t("offlineRecording.errors.reselectSettings")}
        </p>
      )}
      {error && (
        <p className="text-sm leading-5 text-error" role="alert">
          {error === SETTINGS_REJECTED || isRawValidationError(error)
            ? t("offlineRecording.card.settingsRejected", SETTINGS_REJECTED)
            : error}
        </p>
      )}
      {recording.status === "uncertain" && (
        <a
          className="inline-flex min-h-11 items-center self-start text-sm font-medium underline underline-offset-4 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          href="/visits"
          rel="noreferrer"
          target="_blank"
        >
          {t("visits.submission.check", "Check Visits")}
        </a>
      )}
      {kind === "recovered" && recording.submitAttempted && (
        <p className="text-sm leading-5 text-muted-foreground">
          {t(
            "offlineRecording.card.submitAttempted",
            "A save was started for this recording and may have reached ROSC. Check Visits before processing it again.",
          )}
        </p>
      )}
      {recording.storageError && (
        <p className="text-sm leading-5 text-muted-foreground">
          {t(
            "offlineRecording.card.partial",
            "The device ran out of space during this recording, so the end may be missing.",
          )}
        </p>
      )}
      {!obsolete && isExpiringSoon(recording.createdAt, now) && (
        <p className="text-sm leading-5 text-muted-foreground">
          {t(
            "offlineRecording.card.expiring",
            "Will be deleted from this device on {{date}}.",
            { date: formatExpiry(recording.createdAt) },
          )}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {kind === "recovered" &&
          onProcess &&
          !(obsolete && (recording.upload || recording.submitAttempted)) && (
            <Button
              className="min-h-11 flex-1 basis-full sm:basis-auto"
              disabled={busy}
              onClick={onProcess}
            >
              {t("offlineRecording.actions.process", "Process")}
            </Button>
          )}
        {canRetry && onRetry && (
          <Button
            className="min-h-11 flex-1 basis-full sm:basis-auto"
            disabled={busy || (!online && recording.status !== "uncertain")}
            onClick={onRetry}
          >
            <RotateCw aria-hidden="true" className="mr-2 h-4 w-4" />
            {recording.status === "uncertain"
              ? t("offlineRecording.actions.sendAgain", "Send again")
              : t("offlineRecording.actions.retry", "Retry")}
          </Button>
        )}
        <Button
          className="min-h-11 flex-1"
          disabled={busy}
          variant="outline"
          onClick={onDownload}
        >
          <Download aria-hidden="true" className="mr-2 h-4 w-4" />
          {t("offlineRecording.actions.download", "Download")}
        </Button>
        <Button
          className="min-h-11 flex-1 text-error hover:text-error"
          disabled={busy}
          variant="outline"
          onClick={onDiscard}
        >
          <Trash2 aria-hidden="true" className="mr-2 h-4 w-4" />
          {t("offlineRecording.actions.discard", "Discard")}
        </Button>
      </div>
    </article>
  );
};
